import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { formatAiFoundryDebugLogAsMarkdown, type AiFoundryDebugLogEntry } from '../hooks/useAiFoundryDebugLog';
import { useAiActivity } from './AiActivityProvider';

const TEXT_PREVIEW_LENGTH = 120;
const DOCK_WIDTH_CLASS = 'w-full sm:w-[28rem]';

function formatTimestamp(date: Date): string {
  return date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trimEnd()}…`;
}

function AiFoundryDebugLogRow({ entry }: { entry: AiFoundryDebugLogEntry }) {
  const isPrompt = entry.direction === 'prompt';
  const preview = truncateText(entry.text.replace(/\s+/g, ' ').trim(), TEXT_PREVIEW_LENGTH);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const copyResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (copyResetTimerRef.current != null) clearTimeout(copyResetTimerRef.current);
  }, []);

  const handleCopy = async () => {
    if (copyResetTimerRef.current != null) clearTimeout(copyResetTimerRef.current);

    try {
      await navigator.clipboard.writeText(formatAiFoundryDebugLogAsMarkdown([entry]));
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }

    copyResetTimerRef.current = setTimeout(() => setCopyState('idle'), 2000);
  };

  return (
    <li className="rounded-md border border-gray-200 bg-white p-3 text-sm">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span
          className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${
            isPrompt ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
          }`}
        >
          {isPrompt ? 'Prompt' : 'Response'}
        </span>
        <span className="font-mono text-xs text-gray-600">{entry.endpointSuffix}</span>
        <span className="ml-auto text-xs text-gray-400">{formatTimestamp(entry.timestamp)}</span>
        <button
          type="button"
          onClick={handleCopy}
          className="text-xs text-action-blue hover:underline"
        >
          {copyState === 'copied' ? 'Copied!' : copyState === 'failed' ? 'Failed' : 'Copy'}
        </button>
      </div>
      <details className="group">
        <summary className="cursor-pointer list-none text-xs text-gray-500 marker:content-none">
          <span className="group-open:hidden">{preview || '(empty)'}</span>
          <span className="hidden group-open:inline">Hide full text</span>
        </summary>
        <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded bg-gray-50 p-2 text-xs text-gray-800">
          {entry.text || '(empty)'}
        </pre>
      </details>
    </li>
  );
}

export function AiActivityDock() {
  const { isOpen, setIsOpen, entries, clearEntries } = useAiActivity();
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const listEndRef = useRef<HTMLLIElement | null>(null);
  const copyResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isOpen || entries.length === 0) return;
    listEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [isOpen, entries.length]);

  useEffect(() => () => {
    if (copyResetTimerRef.current != null) clearTimeout(copyResetTimerRef.current);
  }, []);

  if (!isOpen) return null;

  const handleCopyAll = async () => {
    if (entries.length === 0) return;
    if (copyResetTimerRef.current != null) clearTimeout(copyResetTimerRef.current);

    try {
      await navigator.clipboard.writeText(formatAiFoundryDebugLogAsMarkdown(entries));
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }

    copyResetTimerRef.current = setTimeout(() => setCopyState('idle'), 2000);
  };

  return (
    <aside
      className={`flex h-1/2 min-h-0 shrink-0 flex-col border-t border-gray-200 bg-white sm:h-full sm:border-l sm:border-t-0 ${DOCK_WIDTH_CLASS}`}
      aria-label="AI activity log"
    >
      <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-4 py-3">
        <h2 className="text-base font-semibold text-gray-900">AI activity</h2>
        <button
          type="button"
          onClick={() => setIsOpen(false)}
          className="rounded-sm p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          aria-label="Close AI activity panel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex shrink-0 items-center justify-end gap-3 border-b border-gray-200 px-4 py-2">
        <button
          type="button"
          onClick={handleCopyAll}
          disabled={entries.length === 0}
          className="text-xs text-action-blue hover:underline disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline"
        >
          {copyState === 'copied' ? 'Copied!' : copyState === 'failed' ? 'Copy failed' : 'Copy All'}
        </button>
        <button
          type="button"
          onClick={clearEntries}
          disabled={entries.length === 0}
          className="text-xs text-action-blue hover:underline disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline"
        >
          Clear
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto bg-gray-50 p-2">
        {entries.length === 0 ? (
          <p className="p-3 text-center text-xs text-gray-500">
            No AI activity yet. Trigger an action that calls the AI.
          </p>
        ) : (
          <ul className="space-y-2">
            {entries.map((entry) => (
              <AiFoundryDebugLogRow key={entry.id} entry={entry} />
            ))}
            <li ref={listEndRef} aria-hidden="true" className="h-px" />
          </ul>
        )}
      </div>
    </aside>
  );
}
